import React, { Suspense, useEffect, useRef } from "react";
import { useRoutes, useLocation } from "react-router-dom";
import publicRoutes from "./publicRoutes";
import accountRoutes from "./accountRoutes";
import adminRoutes from "./adminRoutes";
import RouteFallback from "./RouteFallback";
import NotFound from "../pages/NotFound";
import { trackEvent } from "../lib/analytics";

// Threshold above which we treat a Suspense fallback as a degraded
// experience worth flagging in product analytics. Anything < 500ms is
// usually invisible to users and not worth the noise.
const SLOW_CHUNK_THRESHOLD_MS = 500;

/**
 * Tracks how long the route-level <Suspense> fallback was visible.
 * If it exceeds the threshold, fires a `chunk_load_slow` analytics
 * event with the destination pathname so we can spot CDN regressions
 * before they hurt conversion.
 */
function ChunkLoadProbe() {
  const location = useLocation();
  const startedAtRef = useRef(performance.now());

  useEffect(() => {
    // Reset timer on every navigation: the probe remounts under
    // Suspense each time a lazy route is requested.
    startedAtRef.current = performance.now();
  }, [location.pathname]);

  useEffect(() => {
    return () => {
      const elapsed = performance.now() - startedAtRef.current;
      if (elapsed > SLOW_CHUNK_THRESHOLD_MS) {
        trackEvent("chunk_load_slow", {
          path: location.pathname,
          elapsed_ms: Math.round(elapsed),
          threshold_ms: SLOW_CHUNK_THRESHOLD_MS,
        });
      }
    };
    // We deliberately depend only on pathname so cleanup fires per
    // route transition, not on every re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return <RouteFallback />;
}

function RoutesTree() {
  return useRoutes([
    ...publicRoutes,
    ...accountRoutes,
    ...adminRoutes,
    { path: "*", element: <NotFound /> },
  ]);
}

export default function AppRoutes() {
  return (
    <Suspense fallback={<ChunkLoadProbe />}>
      <RoutesTree />
    </Suspense>
  );
}
